import { Component, EventEmitter, Inject, OnDestroy, Output, ViewEncapsulation, inject, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { DialogComponent } from '@components/layouts/dialog/dialog.component';
import { LanguageService, SupportedLanguage } from '@services/language.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

const ERSD_LANGUAGES: { value: SupportedLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Dutch' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'hi', label: 'Hindi' },
];

@Component({
  selector: 'kzn-ersd-acceptance',
  standalone: true,
  imports: [DialogComponent, MatExpansionModule, MatCheckboxModule, ReactiveFormsModule, CommonModule, FormsModule],
  templateUrl: './ersd-acceptance.component.html',
  styleUrl: './ersd-acceptance.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ErsdAcceptanceComponent implements OnDestroy {
  @Output() confirmed = new EventEmitter<boolean>();
  @Output() openDeclineDialogEvent = new EventEmitter<any>();

  public ersdAcceptance: FormControl = new FormControl(false, Validators.required);
  public safeEulaMessage: SafeHtml = '';
  readonly languageOptions = ERSD_LANGUAGES;
  currentLanguage: SupportedLanguage = 'en';
  private _languageService = inject(LanguageService);
  private _cdr = inject(ChangeDetectorRef);
  private _langSubscription: Subscription = new Subscription();

  constructor(
    private sanitizer: DomSanitizer,
    private dialogRef: MatDialogRef<ErsdAcceptanceComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { requesterName: string; customerName: string; requesterMessage: string; EULAMessage: string, openDeclineConfirmationDialog: () => void }
  ) {
    this.safeEulaMessage = this.sanitizer.bypassSecurityTrustHtml(this.data?.EULAMessage || '');
    this.currentLanguage = this._languageService.currentLanguage;
    this._langSubscription.add(
      this._languageService.language$.subscribe((lang) => {
        this.currentLanguage = lang;
        // this._cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this._langSubscription.unsubscribe();
  }

  t(key: string): string {
    return this._languageService.instant(key);
  }

  tParams(key: string, params: Record<string, string>): string {
    return this._languageService.instant(key, params);
  }

  get ersdAgreeCheckboxHtml(): SafeHtml {
    const text = this.t('ersdAgreeCheckbox') || '';
    if (text.includes('<strong>')) {
      return this.sanitizer.bypassSecurityTrustHtml(text);
    }
    const phrase = 'Electronic Record and Signature Disclosure';
    const html = text.includes(phrase)
      ? text.replace(phrase, `<strong>${phrase}</strong>`)
      : text;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onLanguageChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const allowed: SupportedLanguage[] = ['en', 'nl', 'es', 'fr', 'de', 'hi'];
    const value = (target?.value as SupportedLanguage) ?? 'en';
    if (allowed.includes(value)) {
      this._languageService.setLanguage(value);
    }
  }

  onLanguageSelect(value: SupportedLanguage): void {
    const allowed: SupportedLanguage[] = ['en', 'nl', 'es', 'fr', 'de', 'hi'];
    if (allowed.includes(value)) {
      this._languageService.setLanguage(value);
    }
  }

  onAction() {
    this.confirmed.emit(true);
    this.dialogRef.close();
  }

  openDeclineDialog() {
    this.dialogRef.afterClosed().subscribe(() => {
      this.data.openDeclineConfirmationDialog();
    });
    this.dialogRef.close();
  }
}
